<script setup lang="ts">
/** 模块无关的顶部导航、内容区、页脚框架。 */
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/vue'
import { Menu, X } from '@lucide/vue'
import { sortedModules } from './module-registry'
import { useSessionStore } from '@/core/auth/session'
import AppButton from '@/core/ui/AppButton.vue'
import AppSidebar from './AppSidebar.vue'
import AppTopBar from './AppTopBar.vue'
import AppFooter from './AppFooter.vue'

const route = useRoute()
const session = useSessionStore()
const mobileNavOpen = ref(false)
const modules = computed(() => sortedModules())
watch(() => route.fullPath, () => { mobileNavOpen.value = false })
onMounted(() => { if (!session.checked) void session.refresh() })
</script>

<template>
  <div class="portal-layout">
    <a class="skip-link" href="#main-content">跳转到主要内容</a>
    <AppTopBar :modules="modules">
      <template #nav-trigger>
        <AppButton variant="ghost" size="sm" class="portal-menu-trigger" aria-label="打开导航" @click="mobileNavOpen = true">
          <Menu :size="21" aria-hidden="true" />
        </AppButton>
      </template>
    </AppTopBar>
    <Dialog :open="mobileNavOpen" class="relative z-50 md:hidden" @close="mobileNavOpen = false">
      <div class="pf-dialog-backdrop" aria-hidden="true" />
      <div class="portal-drawer-viewport">
        <DialogPanel class="portal-drawer">
          <div class="portal-drawer__heading">
            <DialogTitle class="portal-drawer__title">浏览平台</DialogTitle>
            <AppButton variant="ghost" size="sm" aria-label="关闭导航" @click="mobileNavOpen = false"><X :size="20" aria-hidden="true" /></AppButton>
          </div>
          <AppSidebar :modules="modules" />
        </DialogPanel>
      </div>
    </Dialog>
    <main id="main-content" class="pf-container portal-content" tabindex="-1">
      <RouterView />
    </main>
    <AppFooter />
  </div>
</template>

<style scoped>
.portal-layout { display: flex; min-height: 100dvh; flex-direction: column; }
.portal-content { flex: 1; padding-block: 30px 48px; outline: none; }
.portal-menu-trigger { display: none; padding: 7px; }
.portal-drawer-viewport { position: fixed; inset: 0; display: flex; }
.portal-drawer { width: min(320px, 85vw); height: 100%; overflow-y: auto; background: var(--pf-surface); padding: 20px; }
.portal-drawer__heading { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; }
.portal-drawer__title { color: var(--pf-text-tertiary); font-size: 13px; }
.skip-link { position: fixed; top: 8px; left: 16px; z-index: 60; transform: translateY(-160%); padding: 10px 16px; border-radius: var(--pf-radius-control); background: var(--pf-accent); color: white; }
.skip-link:focus { transform: translateY(0); }
@media (max-width: 767px) {
  .portal-menu-trigger { display: inline-flex; }
  .portal-content { padding-block: 24px 32px; }
}
</style>
